import { useTranslation } from "@shared/utils/internationalization";
import Box from "@ui-kit/layout/Box/Box.web";
import HStack from "@ui-kit/layout/Stack/HStack.web";
import Text from "@ui-kit/typography/Text/Text.web";
import {
  RealtimeConnectionInfo,
  RealtimeConnectionState,
} from "../data/types/realtime";
import { REALTIME_CONNECTION_STATES } from "../constants/realtime-connection-constants";

// TODO: Add dummy trans tags and fix unit tests approach
// so that module-level t() calls don't break other tests
const CONNECTION_STATUS_CONFIG: Record<
  RealtimeConnectionState,
  { color: string; labelKey: string }
> = {
  [REALTIME_CONNECTION_STATES.CONNECTED]: {
    color: "green.500",
    // labelKey: t("Live"),
    labelKey: "Live",
  },
  [REALTIME_CONNECTION_STATES.CONNECTING]: {
    color: "yellow.500",
    // labelKey: t("Connecting"),
    labelKey: "Connecting",
  },
  [REALTIME_CONNECTION_STATES.RECONNECTING]: {
    color: "yellow.500",
    // labelKey: t("Reconnecting"),
    labelKey: "Reconnecting",
  },
  [REALTIME_CONNECTION_STATES.DISCONNECTED]: {
    color: "gray.400",
    // labelKey: t("Offline"),
    labelKey: "Offline",
  },
  [REALTIME_CONNECTION_STATES.ERROR]: {
    color: "red.500",
    // labelKey: t("Error"),
    labelKey: "Error",
  },
};

const CONNECTION_INDICATOR_SIZE_CONFIG = {
  sm: {
    dotSize: "element.xs",
    fontSize: "xs",
  },
  md: {
    dotSize: "element.sm",
    fontSize: "sm",
  },
} as const;

type RealtimeConnectionIndicatorProps = {
  connectionInfo: RealtimeConnectionInfo;
  showLabel?: boolean;
  size?: "sm" | "md";
  hiddenStates?: RealtimeConnectionState[];
};

export function RealtimeConnectionIndicator({
  connectionInfo,
  showLabel = true,
  size = "sm",
  hiddenStates,
}: RealtimeConnectionIndicatorProps): React.ReactElement | null {
  const { t } = useTranslation("common");

  if (hiddenStates?.includes(connectionInfo.state)) {
    return null;
  }

  const config = CONNECTION_STATUS_CONFIG[connectionInfo.state];
  const sizeConfig = CONNECTION_INDICATOR_SIZE_CONFIG[size];
  const label = t(config.labelKey);

  return (
    <Box
      role="status"
      aria-live="polite"
      bg="bg"
      borderRadius="md"
      px="sm"
      py="xs"
      boxShadow="sm"
    >
      <HStack
        gap="xs"
        title={t("Real-time updates: {{status}}", { status: label })}
      >
        <Box
          w={sizeConfig.dotSize}
          h={sizeConfig.dotSize}
          borderRadius="full"
          bg={config.color}
          opacity={
            connectionInfo.state === REALTIME_CONNECTION_STATES.CONNECTED
              ? 1
              : 0.8
          }
        />
        {showLabel && (
          <Text fontSize={sizeConfig.fontSize} color="fg.muted">
            {label}
          </Text>
        )}
      </HStack>
    </Box>
  );
}
